import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { inked, toon } from "../toon";
import type { SkaterLook } from "./look";
import { cachedShape, clipTo } from "./geom";
import { cloth } from "./materials";
import { buildBuckle } from "./outfit";
import { beltGeometry, pelvisSize, trunkShell, WAIST_Y } from "./trunk";

// Outfits for the realistic rider (#75), on the shaped torso: a tank top
// cut from the trunk's shape with ribbed binding round its armholes and
// neck; a neckerchief draped over the chest, and a western shirt's sawtooth yoke,
// pointed pocket flaps, placket and pearl snaps; a space suit's patches
// and chest control module; a belt on the shaped pelvis. Torso space (up +y, chest
// toward -x). `chest(y, z)` is where the shaped chest is (`trunkFront`), so
// everything lies on it instead of floating as a flat part in front.

/** Where the shaped chest is (its x, toward -x) at height y and z across. */
export type Chest = (y: number, z: number) => number;

/** The neckerchief's band goes round the neck just above where the trunk ends, dipping in front. */
const BAND = { y: 0.556, dip: 0.012, radius: 0.057, tube: 0.012 };
/** The drape: tucked under the band, out across the collarbones, down to a point on the chest. */
const DRAPE = { top: 0.535, point: 0.335, half: 0.088 };

/**
 * A grid over (u across -1..1, v down 0..1) lying `out(u, v)` in front of
 * the chest, placed by `at(u, v)` -> [y, z]. Wound to face out (-x).
 */
function onChest(chest: Chest, cols: number, rows: number, at: (u: number, v: number) => [number, number], out: (u: number, v: number) => number): THREE.BufferGeometry {
  const pos: number[] = [];
  const uv: number[] = [];
  for (let i = 0; i <= cols; i++) {
    const u = -1 + (2 * i) / cols;
    for (let j = 0; j <= rows; j++) {
      const v = j / rows;
      const [y, z] = at(u, v);
      pos.push(chest(y, z) - out(u, v), y, z);
      // One unit per 20 cm, like the lofted parts, so the weave keeps its scale.
      uv.push(z / 0.2, y / 0.2);
    }
  }
  const index: number[] = [];
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      const a = i * (rows + 1) + j;
      const b = (i + 1) * (rows + 1) + j;
      // (a, a+1, b): across is +z, down is -y, so this faces -x.
      index.push(a, a + 1, b, b, a + 1, b + 1);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(index);
  geo.computeVertexNormals();
  return geo;
}

/** A thin tube along points given as [y, z] on the chest, `out` in front of it. */
function piping(chest: Chest, pts: [number, number][], out: number, radius: number): THREE.BufferGeometry {
  // Sample each straight run densely so the curve keeps the corners sharp.
  const path: THREE.Vector3[] = [];
  for (let k = 0; k < pts.length - 1; k++) {
    const [y0, z0] = pts[k]!;
    const [y1, z1] = pts[k + 1]!;
    const n = 8;
    for (let s = 0; s < n; s++) {
      const t = s / n;
      const y = y0 + (y1 - y0) * t;
      const z = z0 + (z1 - z0) * t;
      path.push(new THREE.Vector3(chest(y, z) - out, y, z));
    }
  }
  const [yl, zl] = pts[pts.length - 1]!;
  path.push(new THREE.Vector3(chest(yl, zl) - out, yl, zl));
  return new THREE.TubeGeometry(new THREE.CatmullRomCurve3(path, false, "centripetal"), path.length * 2, radius, 6, false);
}

/**
 * The neckerchief: a rolled band round the neck, lower in front; the
 * triangle of cloth tucked under it and draped over the chest to a point,
 * with soft folds fanning down from the band; a knot and two short tails
 * at the back of the neck. One mesh.
 */
export function neckerchiefGeometry(chest: Chest): THREE.BufferGeometry {
  // The band: a tube round the neck, dipping toward the front (-x).
  const ring: THREE.Vector3[] = [];
  for (let i = 0; i < 32; i++) {
    const a = (i / 32) * Math.PI * 2;
    const front = Math.max(0, Math.cos(a));
    ring.push(new THREE.Vector3(-Math.cos(a) * BAND.radius, BAND.y - BAND.dip * front * front, Math.sin(a) * BAND.radius));
  }
  const band = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(ring, true), 48, BAND.tube, 8, true);

  // The drape: its bottom edges bow out a little, so it reads as cloth, not a cut triangle.
  const drape = onChest(
    chest,
    16,
    18,
    (u, v) => {
      const y = DRAPE.top - v * (DRAPE.top - DRAPE.point);
      return [y, u * DRAPE.half * (1 - v) ** 0.85];
    },
    // Folds fan down from the band and fade toward the point; it stands off the chest a little more lower down.
    (u, v) => 0.0035 + 0.003 * v + 0.0035 * (1 - v) * (0.5 + 0.5 * Math.cos(u * Math.PI * 2.5)),
  );

  // The knot at the back, and two limp ends hanging from it, splayed a little.
  const knot = new THREE.SphereGeometry(1, 12, 8);
  knot.scale(0.016, 0.015, 0.018);
  knot.translate(BAND.radius + 0.008, BAND.y, 0);
  const tails = [-1, 1].map((side) => {
    const tail = new THREE.SphereGeometry(1, 10, 8);
    tail.translate(0, -1, 0); // hang from the top
    tail.scale(0.005, 0.026, 0.014);
    tail.rotateX(side * 0.4);
    tail.translate(BAND.radius + 0.012, BAND.y - 0.006, side * 0.006);
    return tail;
  });
  const parts = [band, drape, knot, ...tails];
  const merged = mergeGeometries(parts)!;
  for (const p of parts) p.dispose();
  return merged;
}

/** A realistic neckerchief in `color`, knotted at the back of the neck. */
export function buildNeckerchief(color: string, torso: THREE.Group, chest: Chest): void {
  const mesh = inked(neckerchiefGeometry(chest), cloth(color), 0.008);
  mesh.name = "bandana";
  torso.add(mesh);
}

/** One side's yoke seam, [y, z] from the shoulder in to the placket, dipping to a point. */
function yokeSeam(side: number): [number, number][] {
  return [
    [0.44, side * 0.17],
    [0.41, side * 0.115],
    [0.372, side * 0.075],
    [0.405, side * 0.04],
    [0.415, side * 0.022],
  ];
}

/** The placket's half-width: the strip down the front the snaps close. */
const PLACKET = 0.018;
/** The pocket flaps: centered this far either side, `top` edge, half-width, and how far the point hangs below the sides. */
const FLAP = { z: 0.088, top: 0.35, half: 0.04, side: 0.02, point: 0.018 };
/** Pearl snaps down the placket (heights), clear of the neckerchief's point. */
const SNAP_Y = [0.09, 0.165, 0.24, 0.315];
const SNAP = { radius: 0.0105, depth: 0.0045 };

/** A pocket flap on one side: straight across the top, pointed at the bottom (a western "sawtooth" flap). */
export function pocketFlapGeometry(chest: Chest, side: number): THREE.BufferGeometry {
  return onChest(
    chest,
    10,
    4,
    (u, v) => {
      const bottom = FLAP.top - FLAP.side - FLAP.point * (1 - Math.abs(u));
      return [FLAP.top - v * (FLAP.top - bottom), side * FLAP.z + u * FLAP.half];
    },
    // Thicker at the seam along the top, like a folded-over flap.
    (_u, v) => 0.0045 - 0.0015 * v,
  );
}

/**
 * A western shirt's details on the shaped chest: yoke piping in the trim
 * color, a placket down the front, pointed pocket flaps in the trim color,
 * and flat pearl snaps (on the placket and each flap's point) in one draw.
 */
export function buildWesternShirt(top: SkaterLook["top"], torso: THREE.Group, chest: Chest): void {
  const trim = top.trim ?? "#f1e3c2";
  const seams = [-1, 1].map((side) => piping(chest, yokeSeam(side), 0.002, 0.0035));
  const yoke = inked(mergeGeometries(seams)!, cloth(trim), 0.004);
  for (const s of seams) s.dispose();
  yoke.name = "yoke";
  torso.add(yoke);

  const placket = inked(
    onChest(
      chest,
      2,
      12,
      (u, v) => [0.43 - v * 0.39, u * PLACKET],
      () => 0.0025,
    ),
    cloth(top.color),
    0.004,
  );
  placket.name = "placket";
  torso.add(placket);

  const flapMat = cloth(trim);
  for (const side of [-1, 1]) {
    const flap = inked(pocketFlapGeometry(chest, side), flapMat, 0.006);
    flap.name = "pocket";
    torso.add(flap);
  }

  const pearls: THREE.BufferGeometry[] = [];
  const pearl = (y: number, z: number, out: number) => {
    const g = new THREE.SphereGeometry(1, 12, 6);
    g.scale(SNAP.depth, SNAP.radius, SNAP.radius);
    g.translate(chest(y, z) - out, y, z);
    pearls.push(g);
  };
  for (const y of SNAP_Y) pearl(y, 0, 0.0025 + SNAP.depth * 0.6);
  for (const side of [-1, 1]) pearl(FLAP.top - FLAP.side - FLAP.point + 0.008, side * FLAP.z, 0.004 + SNAP.depth * 0.6);
  const snaps = new THREE.Mesh(mergeGeometries(pearls)!, toon("#fbf7ee", { roughness: 0.15, metalness: 0.2 }));
  for (const g of pearls) g.dispose();
  snaps.name = "snaps";
  torso.add(snaps);
}

/**
 * A disc of `radius` laid on a surface: `place(s, h)` maps a point on the
 * flat disc (s across, h up, from its center) to where it lies. Faces
 * `outward` at its center.
 */
function laidDisc(radius: number, place: (s: number, h: number) => THREE.Vector3, outward: THREE.Vector3): THREE.BufferGeometry {
  const RINGS = 6;
  const AROUND = 24;
  const pos: number[] = [];
  const uv: number[] = [];
  const at = (s: number, h: number) => {
    const p = place(s, h);
    pos.push(p.x, p.y, p.z);
    uv.push(0.5 + s / (2 * radius), 0.5 + h / (2 * radius));
  };
  at(0, 0);
  for (let r = 1; r <= RINGS; r++) {
    for (let i = 0; i < AROUND; i++) {
      const a = (i / AROUND) * Math.PI * 2;
      at(Math.cos(a) * radius * (r / RINGS), Math.sin(a) * radius * (r / RINGS));
    }
  }
  const index: number[] = [];
  const ring = (r: number, i: number) => 1 + (r - 1) * AROUND + (i % AROUND);
  for (let i = 0; i < AROUND; i++) index.push(0, ring(1, i), ring(1, i + 1));
  for (let r = 1; r < RINGS; r++) {
    for (let i = 0; i < AROUND; i++) index.push(ring(r, i), ring(r + 1, i), ring(r + 1, i + 1), ring(r, i), ring(r + 1, i + 1), ring(r, i + 1));
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(index);
  geo.computeVertexNormals();
  const n = new THREE.Vector3().fromBufferAttribute(geo.getAttribute("normal"), 0);
  if (n.dot(outward) < 0) {
    // Wound the wrong way for this surface: flip it.
    for (let k = 0; k < index.length; k += 3) [index[k + 1], index[k + 2]] = [index[k + 2]!, index[k + 1]!];
    geo.setIndex(index);
    geo.computeVertexNormals();
  }
  return geo;
}

/**
 * An embroidered patch laid on a surface: the colored disc a hair proud of
 * it (`place(s, h, out)`), and its white border as a raised cord round the
 * edge, as a child. Named `patch`, like the toy's.
 */
function laidPatch(color: string, radius: number, place: (s: number, h: number, out: number) => THREE.Vector3, outward: THREE.Vector3): THREE.Mesh {
  const patch = new THREE.Mesh(
    laidDisc(radius, (s, h) => place(s, h, 0.0015), outward),
    toon(color, { roughness: 0.8 }, "cloth"),
  );
  patch.name = "patch";
  const edge: THREE.Vector3[] = [];
  for (let i = 0; i < 40; i++) {
    const a = (i / 40) * Math.PI * 2;
    edge.push(place(Math.cos(a) * radius, Math.sin(a) * radius, 0.002));
  }
  const rim = new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(edge, true), 60, 0.003, 6, true), toon("#ffffff", { roughness: 0.8 }, "cloth"));
  patch.add(rim);
  return patch;
}

/** The mission patch on the space suit's chest (torso space), lying on the shaped chest. */
const CHEST_PATCH = { y: 0.34, z: 0.07, radius: 0.04 };

/**
 * A mission patch wrapped round the upper arm (shoulder space: the arm
 * hangs down -y), on its outer side (+z for `side` 1). `radius(y)` is the
 * arm's radius at a height.
 */
export function buildArmPatch(color: string, side: number, radius: (y: number) => number): THREE.Mesh {
  const Y = -0.06;
  const R = radius(Y);
  return laidPatch(
    color,
    0.042,
    (s, h, out) => {
      // s runs round the arm toward the front (-x), as an arc on it.
      const r = radius(Y + h) + out;
      const a = s / R;
      return new THREE.Vector3(-Math.sin(a) * r, Y + h, side * Math.cos(a) * r);
    },
    new THREE.Vector3(0, 0, side),
  );
}

/** The chest-mounted control module: depth, height and width; its back sits on the chest at `y`. */
const PACK = { y: 0.2, d: 0.04, h: 0.085, w: 0.15 };

/**
 * A space suit's details on the shaped chest: a mission patch lying on it,
 * and a chest-mounted control module (a rounded box whose back sits on
 * the chest, two knobs on top, a recessed display, three flush LEDs) with
 * an umbilical hose from each side down to the waist.
 */
export function buildSpaceSuit(top: SkaterLook["top"], torso: THREE.Group, chest: Chest): void {
  const casing = toon("#9aa3b5", { roughness: 0.45, metalness: 0.2 }, "plastic");
  const body = new RoundedBoxGeometry(PACK.d, PACK.h, PACK.w, 3, 0.012);
  const knobs = [-1, 1].map((side) => {
    const knob = new THREE.CylinderGeometry(0.009, 0.01, 0.014, 16);
    knob.translate(0.002, PACK.h / 2 + 0.006, side * 0.04);
    // The rounded box isn't indexed; match it so they merge.
    const flat = knob.toNonIndexed();
    knob.dispose();
    return flat;
  });
  const packGeo = mergeGeometries([body, ...knobs])!;
  for (const g of [body, ...knobs]) g.dispose();
  const pack = inked(packGeo, casing, 0.01);
  pack.name = "chest-pack";
  pack.position.set(chest(PACK.y, 0) - PACK.d / 2, PACK.y, 0);
  torso.add(pack);

  // The display: a dark glass pane set into the front face, left of the LEDs.
  const display = new THREE.Mesh(new THREE.BoxGeometry(0.004, 0.042, 0.082), toon("#18202c", { roughness: 0.12, metalness: 0.1 }, "glass"));
  display.name = "pack-display";
  display.position.set(-PACK.d / 2 + 0.0012, 0.006, -0.018);
  pack.add(display);

  // Three LEDs flush in the face, in one draw.
  const ledColors = ["#ef476f", "#06d6a0", "#ffd166"];
  const leds = ledColors.map((c, i) => {
    const led = new THREE.SphereGeometry(1, 10, 6);
    led.scale(0.0022, 0.0055, 0.0055);
    led.translate(-PACK.d / 2, 0.022 - i * 0.015, 0.048);
    const color = new THREE.Color(c);
    const n = led.getAttribute("position").count;
    led.setAttribute("color", new THREE.Float32BufferAttribute(Array.from({ length: n }, () => [color.r, color.g, color.b]).flat(), 3));
    return led;
  });
  const ledMesh = new THREE.Mesh(mergeGeometries(leds)!, new THREE.MeshBasicMaterial({ vertexColors: true }));
  for (const g of leds) g.dispose();
  ledMesh.name = "leds";
  pack.add(ledMesh);

  // Umbilical hoses from the pack's sides, over the chest and down into the suit at the waist.
  const HOSE = 0.009;
  const hoses = [-1, 1].map((side) => {
    const on = (y: number, z: number) => new THREE.Vector3(chest(y, z) - HOSE - 0.003, y, z);
    const pts = [
      new THREE.Vector3(pack.position.x, PACK.y - 0.01, side * (PACK.w / 2 - 0.004)),
      new THREE.Vector3(pack.position.x + 0.004, PACK.y - 0.016, side * (PACK.w / 2 + 0.012)),
      on(0.15, side * 0.095),
      on(0.1, side * 0.1),
      on(0.05, side * 0.095),
    ];
    return new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts, false, "centripetal"), 32, HOSE, 8, false);
  });
  const hose = inked(mergeGeometries(hoses)!, toon("#6b7384", { roughness: 0.55 }, "plastic"), 0.006);
  for (const g of hoses) g.dispose();
  hose.name = "hoses";
  torso.add(hose);

  torso.add(
    laidPatch(
      top.trim ?? "#1d4ed8",
      CHEST_PATCH.radius,
      (s, h, out) => new THREE.Vector3(chest(CHEST_PATCH.y + h, CHEST_PATCH.z + s) - out, CHEST_PATCH.y + h, CHEST_PATCH.z + s),
      new THREE.Vector3(-1, 0, 0),
    ),
  );
}

/**
 * A belt following the shaped pelvis's waistband (hips space, front -x),
 * `width` the pelvis's, with the big buckle out front.
 */
export function buildShapedBelt(bottoms: SkaterLook["bottoms"], hips: THREE.Group, width: number): void {
  const belt = inked(beltGeometry(width), toon("#3a2418", { roughness: 0.6 }, "leather"), 0.008);
  belt.name = "belt";
  hips.add(belt);
  const buckle = buildBuckle(bottoms);
  const y = (WAIST_Y[0] + WAIST_Y[1]) / 2;
  // Its back against the leather, which is 6 mm proud of the waistband.
  buckle.position.set(-(pelvisSize(y).d + 0.006 + 0.008), y, 0);
  hips.add(buckle);
}

/**
 * Where a tank top is cut (torso space, `width` across): the armholes, round
 * each arm's pivot and down to a curve under it, and the neckline, scooped
 * lower in front than at the back. Each is an ellipse across (z) and up (y).
 */
const TANK = {
  arm: { y: 0.44, z: 0.215, ry: 0.15, rz: 0.1 },
  neck: { y: 0.565, rz: 0.078, front: 0.13, back: 0.075 },
  /** How far the cloth and its binding stand off the skin. */
  out: 0.0025,
  bindingOut: 0.004,
  /** The binding's width, in the cut's units (about meters). */
  binding: 0.008,
};

/** Inside a tank top's cloth where this is >= 0, about meters from its nearest edge; the armholes and neckline are below 0. */
export function tankCut(p: THREE.Vector3, width = 1): number {
  const { arm, neck } = TANK;
  const z = Math.abs(p.z) / width;
  const armhole = (Math.hypot((p.y - arm.y) / arm.ry, (z - arm.z) / arm.rz) - 1) * arm.rz;
  // The neckline's depth eases from the back's to the front's across the sides.
  const ry = neck.back + (neck.front - neck.back) * (0.5 - 0.5 * Math.tanh(p.x / 0.03));
  const neckline = (Math.hypot((p.y - neck.y) / ry, z / neck.rz) - 1) * neck.rz;
  return Math.min(armhole, neckline);
}

/**
 * A tank top over the bare trunk: the trunk's shape a hair proud of it, cut
 * to curved armholes and a scoop neck, with a ribbed binding a touch
 * prouder round every edge. Its bottom tucks into the shorts.
 */
export function buildTankTop(top: SkaterLook["top"], torso: THREE.Group, width: number): void {
  const tank = new THREE.Mesh(cachedShape(`tank-top ${width}`, () => tankTopGeometry(width)), cloth(top.color));
  tank.name = "tank-top";
  torso.add(tank);
  // Self-fabric binding, a shade darker where the rib's folded edge catches less light.
  const binding = new THREE.Mesh(cachedShape(`tank-binding ${width}`, () => tankBindingGeometry(width)), cloth(`#${new THREE.Color(top.trim ?? top.color).multiplyScalar(0.86).getHexString()}`));
  binding.name = "tank-binding";
  torso.add(binding);
}

/** The tank top's cloth: the trunk's shape a hair proud, cut to its armholes and neckline. */
export function tankTopGeometry(width: number): THREE.BufferGeometry {
  const shell = trunkShell(width, TANK.out, 2);
  const cut = clipTo(shell, (p) => tankCut(p, width));
  shell.dispose();
  return cut;
}

/** The binding: the cloth's edge band, pushed out along the normals to stand a touch prouder. */
export function tankBindingGeometry(width: number): THREE.BufferGeometry {
  const cut = tankTopGeometry(width);
  const band = clipTo(cut, (p) => TANK.binding - tankCut(p, width));
  cut.dispose();
  const P = band.getAttribute("position");
  const N = band.getAttribute("normal");
  const lift = TANK.bindingOut - TANK.out;
  const n = new THREE.Vector3();
  for (let i = 0; i < P.count; i++) {
    n.fromBufferAttribute(N, i).normalize().multiplyScalar(lift);
    P.setXYZ(i, P.getX(i) + n.x, P.getY(i) + n.y, P.getZ(i) + n.z);
  }
  return band;
}
